/* ==========================================================================
   PatternUploader — file validation, canvas compositing and image upload for
   the custom-pattern roller shade (see ui/shades.md §3.3 / §7).

   Two things get uploaded per saved pattern:
     1. the ORIGINAL file, untouched — production prints from this
     2. a COMPOSITED PREVIEW (fabric + positioning applied) — order review

   Config comes from window.CURTARRA_PATTERN_UPLOAD, set in
   snippets/pattern-customizer.liquid:
     { signEndpoint, publicKey, legacyAuth, folder }

   signEndpoint (preferred): a URL returning {token, expire, signature}. The
   signature is produced server-side from the ImageKit PRIVATE key, so the
   private key never reaches the browser.
   legacyAuth (fallback): the pre-existing client-side Basic auth used by
   snippets/product-variants-v13.liquid. Kept so this module works before the
   signing endpoint exists — see the warning in the snippet.
   ========================================================================== */

(function () {
  'use strict';

  var ENDPOINT = 'https://upload.imagekit.io/api/v1/files/upload';
  /* iOS Safari caps a single canvas at ~16.7M px; stay well under it. */
  var MAX_SOURCE_EDGE = 2400;
  var PREVIEW_EDGE = 1400;

  function cfg() {
    return window.CURTARRA_PATTERN_UPLOAD || {};
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /* ── validation ───────────────────────────────────────────────────────── */
  function validate(file, opts) {
    opts = opts || {};
    var accept = opts.accept || ['image/jpeg', 'image/png', 'image/webp'];
    var acceptExt = opts.acceptExt || ['jpg', 'jpeg', 'png', 'webp'];
    var maxMB = opts.maxMB || 40;

    if (!file) return 'Please choose an image file.';
    /* Safari sometimes hands over an empty file.type — fall back to the
       extension rather than rejecting a perfectly good JPEG. */
    var typeOk = file.type
      ? accept.indexOf(file.type) !== -1
      : acceptExt.indexOf(String(file.name || '').split('.').pop().toLowerCase()) !== -1;
    if (!typeOk) {
      return 'Unsupported file type. Please upload a JPG, PNG or WEBP image.';
    }
    if (file.size > maxMB * 1024 * 1024) {
      return 'This file is ' + (file.size / 1048576).toFixed(1) + ' MB. The limit is ' + maxMB + ' MB.';
    }
    return null;
  }

  /* ── image loading ────────────────────────────────────────────────────── */
  function loadImage(src, crossOrigin) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      if (crossOrigin) img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('Could not read the image.')); };
      img.src = src;
    });
  }

  function canvasToBlob(canvas, type, quality) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        blob ? resolve(blob) : reject(new Error('Could not prepare the image preview.'));
      }, type, quality);
    });
  }

  /**
   * Load a user file into an <img> that is safe to keep in memory and to draw
   * on a canvas. Very large files are re-encoded down to MAX_SOURCE_EDGE so
   * that mobile Safari does not blow its canvas budget during compositing.
   * The ORIGINAL file is still uploaded untouched — this copy is display-only.
   *
   * Uses object URLs rather than data URLs for both the original and any resized
   * copy: this works even when the browser reports an empty file.type, and it
   * avoids holding a ~1.37x base64 copy in memory. Callers own the returned
   * `objectUrl` and must revoke it when they drop `src`.
   */
  function loadUserImage(file) {
    var url = URL.createObjectURL(file);
    return loadImage(url)
      .then(function (img) {
        var longEdge = Math.max(img.naturalWidth, img.naturalHeight);
        if (longEdge <= MAX_SOURCE_EDGE) return { img: img, src: url, objectUrl: url };

        var k = MAX_SOURCE_EDGE / longEdge;
        var c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(img.naturalWidth * k));
        c.height = Math.max(1, Math.round(img.naturalHeight * k));
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);

        /* JPEG has no alpha channel. Re-encoding every oversized upload as JPEG
           therefore turned transparent PNG/WEBP pixels black in the editor and
           in the composited order preview. Keep JPEG for photographic JPEGs, but
           use PNG for formats that may contain transparency. */
        var mime = String(file.type || '').toLowerCase();
        var ext = String(file.name || '').split('.').pop().toLowerCase();
        var isJpeg = mime ? mime === 'image/jpeg' : ext === 'jpg' || ext === 'jpeg';
        var outputType = isJpeg ? 'image/jpeg' : 'image/png';

        return canvasToBlob(c, outputType, isJpeg ? 0.92 : undefined).then(function (blob) {
          var small = URL.createObjectURL(blob);
          URL.revokeObjectURL(url);
          return loadImage(small)
            .then(function (simg) {
              return { img: simg, src: small, objectUrl: small };
            })
            .catch(function (e) {
              URL.revokeObjectURL(small);
              throw e;
            });
        });
      })
      .catch(function (e) {
        URL.revokeObjectURL(url);
        throw e;
      });
  }

  /* ── swatch colour ────────────────────────────────────────────────────── */
  var colourCache = {};

  /**
   * Fabric colour of a swatch image, as hex.
   *
   * Some fabrics ship only a photo swatch, no hex. Painting that photo behind
   * the shade `cover`-crops it into visible artefacts, and the design calls for
   * a flat fabric base with the woven texture on top. Sampling gives us the
   * real fabric colour as a flat fill — and it keeps the photo off the preview
   * canvas, so a CORS-restricted CDN can never taint the export.
   *
   * Samples the CENTRAL 50% only. Curtarra swatch photos are typically a round
   * fabric sample on a white backdrop; averaging the whole frame drags the
   * result toward white. A centred circle of diameter N contains a square of
   * side 0.707N, so a 0.5N box is always inside the fabric.
   *
   * Resolves to null if the image can't be read — a taint, a load error or a
   * stall. Only successes are cached: a failure here is usually transient (the
   * 6s stall in particular), and caching it meant one slow response left the
   * fabric unresolved for the rest of the session. Callers show the neutral
   * fabric base and may call again.
   */
  function averageColor(url) {
    if (!url) return Promise.resolve(null);
    if (Object.prototype.hasOwnProperty.call(colourCache, url)) {
      return Promise.resolve(colourCache[url]);
    }
    var timeout = new Promise(function (resolve) {
      setTimeout(function () { resolve(null); }, 6000);
    });
    return Promise.race([loadImage(url, true), timeout])
      .then(function (img) {
        if (!img) return null;
        var N = 32;
        var c = document.createElement('canvas');
        c.width = N;
        c.height = N;
        var ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, N, N);
        var lo = Math.floor(N * 0.25);
        var side = Math.max(1, Math.ceil(N * 0.75) - lo);
        var d = ctx.getImageData(lo, lo, side, side).data;   // throws when tainted
        var r = 0, g = 0, b = 0, n = 0;
        for (var i = 0; i < d.length; i += 4) {
          if (d[i + 3] < 8) continue;               // skip transparent pixels
          r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
        }
        if (!n) return null;
        var hex = '#' + [r, g, b].map(function (v) {
          var h = Math.round(v / n).toString(16);
          return h.length < 2 ? '0' + h : h;
        }).join('');
        colourCache[url] = hex;
        return hex;
      })
      .catch(function () {
        return null;
      });
  }

  /* ── compositing ──────────────────────────────────────────────────────── */
  /**
   * Reproduce, on a canvas, exactly what the DOM shows — including the fabric
   * weave and the gradient hardware, not just the artwork. This image is what
   * customer service and production eyeball against the order, so anything the
   * customer saw and it lacks reads as "we made something else".
   *
   * The geometry it mirrors:
   *   .pc-art  { position:absolute; inset:0 }            // 'full'
   *   .pc-art.pc-center { inset:14% 19% }                // 'center'
   *   .pc-art img { object-fit:contain; transform-origin:center;
   *                 transform: translate(tx%,ty%) scale(s) rotate(r) }
   * CSS applies those functions right-to-left, and the translate percentages
   * are relative to the image box (== the art box), so the canvas order below
   * is: move to art-box centre → translate → scale → rotate → draw centred.
   * Clipping happens at the FRAME (.pc-frame has overflow:hidden), not the art
   * box, which is why a 'center' layout can bleed outside its inset.
   */
  function composite(o) {
    var ratio = (o.frameW || 1) / (o.frameH || 1);
    var FW, FH;
    if (ratio >= 1) { FW = PREVIEW_EDGE; FH = Math.round(PREVIEW_EDGE / ratio); }
    else { FH = PREVIEW_EDGE; FW = Math.round(PREVIEW_EDGE * ratio); }

    var withHw = o.withHardware !== false;
    var tubeH = withHw ? Math.max(8, Math.round(FH * 0.042)) : 0;
    var railH = withHw ? Math.max(7, Math.round(FH * 0.034)) : 0;

    var canvas = document.createElement('canvas');
    canvas.width = FW;
    canvas.height = tubeH + FH + railH;
    var ctx = canvas.getContext('2d');

    /* Vertical two-stop gradient, mirroring the `linear-gradient(180deg, …)` the
       DOM bands use. Stops come from patternTubeStops()/patternRailStops() so
       there is one source of truth for both renderers. */
    function band(y, h, stops, fallback) {
      var pair = stops && stops.length === 2 ? stops : fallback;
      var g = ctx.createLinearGradient(0, y, 0, y + h);
      g.addColorStop(0, pair[0]);
      g.addColorStop(1, pair[1]);
      return g;
    }

    /**
     * The weave, mirroring `.pc-fab::before`: 1px lines on a 3px pitch in both
     * axes, the whole overlay at 50% opacity. Painted UNDER the artwork, same as
     * the DOM (where .pc-art follows ::before), so it only shows through where
     * `contain` leaves fabric exposed.
     *
     * The DOM pitch is a fixed 3 CSS px against a fabric at most 196px wide;
     * scaling it by FW keeps the same visual density on a 1400px canvas instead
     * of a weave too fine to see. The 1:3 line-to-gap duty is preserved.
     */
    function weave() {
      var pitch = Math.max(3, Math.round(FW * (3 / 196)));
      var lineW = Math.max(1, Math.round(pitch / 3));
      var i;
      ctx.save();
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = 'rgba(0,0,0,0.04)';
      for (i = 0; i < FW; i += pitch) ctx.fillRect(i, 0, lineW, FH);
      ctx.fillStyle = 'rgba(0,0,0,0.03)';
      for (i = 0; i < FH; i += pitch) ctx.fillRect(0, i, FW, lineW);
      ctx.restore();
    }

    function paint() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      /* hardware bands */
      if (withHw) {
        ctx.fillStyle = band(0, tubeH, o.tubeColors, ['#f3efe8', '#d9d2c6']);
        ctx.fillRect(0, 0, FW, tubeH);
        ctx.fillStyle = band(tubeH + FH, railH, o.railColors, ['#e8e2d8', '#cbc3b6']);
        ctx.fillRect(0, tubeH + FH, FW, railH);
      }

      /* fabric area — clipped, so the printable region is unambiguous */
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, tubeH, FW, FH);
      ctx.clip();
      ctx.translate(0, tubeH);

      /* Flat fill only. An earlier version could `cover`-paint the swatch photo
         here when no colour had resolved; that cropped a round fabric sample into
         a visible oval, disagreed with the DOM preview, and risked tainting the
         canvas from the CDN. averageColor() exists so this stays a flat colour. */
      ctx.fillStyle = o.baseColor || '#efe9df';
      ctx.fillRect(0, 0, FW, FH);
      weave();

      if (o.img) {
        var ax = 0, ay = 0, aw = FW, ah = FH;
        if (o.coverage === 'center') {
          ax = FW * 0.19; ay = FH * 0.14;
          aw = FW * (1 - 0.38); ah = FH * (1 - 0.28);
        }
        /* object-fit: contain inside the art box */
        var k = Math.min(aw / o.img.naturalWidth, ah / o.img.naturalHeight);
        var dw = o.img.naturalWidth * k;
        var dh = o.img.naturalHeight * k;

        ctx.translate(ax + aw / 2, ay + ah / 2);
        ctx.translate(((o.tx || 0) / 100) * aw, ((o.ty || 0) / 100) * ah);
        ctx.scale(o.scale || 1, o.scale || 1);
        ctx.rotate(((o.rotation || 0) * Math.PI) / 180);
        ctx.drawImage(o.img, -dw / 2, -dh / 2, dw, dh);
      }
      ctx.restore();
    }

    function toBlob() {
      return new Promise(function (resolve, reject) {
        try {
          canvas.toBlob(function (blob) {
            blob ? resolve(blob) : reject(new Error('Could not render the preview.'));
          }, 'image/jpeg', 0.9);
        } catch (e) {
          reject(e);
        }
      });
    }

    /* Nothing to await: the only image drawn is the customer's own, which came
       from an object/data URL and so can never taint the canvas. */
    paint();
    return toBlob();
  }

  /* ── upload ───────────────────────────────────────────────────────────── */
  function authFields() {
    var c = cfg();
    if (c.signEndpoint) {
      return fetch(c.signEndpoint, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('Upload authorisation failed (' + r.status + ').');
          return r.json();
        })
        .then(function (j) {
          return { token: j.token || uuid(), expire: j.expire, signature: j.signature };
        });
    }
    return Promise.resolve({ token: uuid(), expire: Math.floor(Date.now() / 1000) + 2400 });
  }

  /**
   * Upload a blob and resolve to its URL.
   *
   * XHR rather than fetch purely for `upload.onprogress`: uploads happen at the
   * add-to-cart click, where a 40 MB original behind a bare spinner reads as a
   * frozen page. onProgress receives a 0..1 fraction.
   */
  function upload(blob, fileName, onProgress) {
    var c = cfg();
    if (!c.publicKey) return Promise.reject(new Error('Upload is not configured.'));

    return authFields().then(function (a) {
      var fd = new FormData();
      fd.append('file', blob, fileName);
      fd.append('fileName', fileName);
      fd.append('publicKey', c.publicKey);
      fd.append('token', a.token);
      fd.append('expire', a.expire);
      if (a.signature) fd.append('signature', a.signature);
      if (c.folder) fd.append('folder', c.folder);
      fd.append('useUniqueFileName', 'true');

      return new Promise(function (resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open('POST', ENDPOINT, true);
        xhr.setRequestHeader('Accept', 'application/json');
        if (!a.signature && c.legacyAuth) {
          xhr.setRequestHeader('Authorization', c.legacyAuth);
        }
        if (onProgress && xhr.upload) {
          xhr.upload.onprogress = function (e) {
            if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
          };
        }
        xhr.onload = function () {
          var json = {};
          try { json = JSON.parse(xhr.responseText); } catch (e) { /* non-JSON body */ }
          if (xhr.status >= 200 && xhr.status < 300 && json.url) {
            if (onProgress) onProgress(1);
            resolve(json.url);
          } else {
            reject(new Error(json.message || 'Upload failed (' + xhr.status + ').'));
          }
        };
        xhr.onerror = function () {
          reject(new Error('Upload failed. Please check your connection and try again.'));
        };
        xhr.onabort = function () { reject(new Error('Upload cancelled.')); };
        xhr.send(fd);
      });
    });
  }

  /** Safe filename stem: strips path/extension and anything exotic. */
  function slugName(name, suffix, ext) {
    var stem = String(name || 'pattern').replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9._-]+/g, '-').slice(0, 60);
    if (!stem) stem = 'pattern';
    return stem + (suffix || '') + '.' + (ext || 'jpg');
  }

  window.PatternUploader = {
    validate: validate,
    loadUserImage: loadUserImage,
    loadImage: loadImage,
    averageColor: averageColor,
    composite: composite,
    upload: upload,
    slugName: slugName,
  };
})();
